import { useRef, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import styled from 'styled-components';
import { Divider, useMediaQuery, Button, Grid } from '@mui/material';
import GooglePlaces from '../common/GooglePlaces';
import { WorksheetTextField, smWidth, xsWidth, Heading } from '../../utils/CommonStyles';
import { ICompletionStatus, IWorksheetPurchaserInfo } from '../../types/worksheet';
import { isAddressInfoFilled } from '../../utils/Validations';
import { scrollToRef } from '../../utils/Functions';

const AddressDetails = (props: ChildProps) => {
  const { completionStatus, fields, handleTextInput, handleDetails, handleNext, disabled, style, asterisk, submitted } = props;
  const { streetAddress, city, province, postalCode, country } = fields;
  const isXS = useMediaQuery(`(max-width: ${xsWidth})`);
  const [attemptedNext, setAttemptedNext] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const showErrors = !!submitted || attemptedNext;
  const cityMissing = showErrors && !city;
  const provinceMissing = showErrors && !province;
  const postalCodeMissing = showErrors && !postalCode;
  const countryMissing = showErrors && !country;

  const handleNextClick = (event: any) => {
    if (isAddressInfoFilled(fields)) {
      handleNext(event);
    } else {
      setAttemptedNext(true);
      scrollToRef(containerRef.current);
    }
  };

  const animationProps = useSpring({
    to: { opacity: completionStatus.personalInfo ? 1 : 0, y: completionStatus.personalInfo ? 0 : 100 },
    from: { opacity: 0, y: 100 },
    delay: 250,
  });

  const InputFields = (
    <Grid container spacing={2}>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <GooglePlaces
          disabled={disabled}
          name={'streetAddress'}
          style={style}
          asterisk={asterisk}
          handleDetails={handleDetails}
          handleTextInput={handleTextInput}
          address={streetAddress}
          submitted={showErrors}
        />
      </Grid>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <WorksheetTextField
          id="city-textarea"
          label="City"
          name="city"
          sx={asterisk}
          error={cityMissing}
          helperText={cityMissing ? 'Missing City' : ''}
          value={city}
          onChange={handleTextInput}
          disabled={disabled}
          required
        />
      </Grid>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <WorksheetTextField
          id="province-textarea"
          label="Province"
          name="province"
          sx={asterisk}
          error={provinceMissing}
          helperText={provinceMissing ? 'Missing Province' : ''}
          value={province}
          onChange={handleTextInput}
          disabled={disabled}
          required
        />
      </Grid>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <WorksheetTextField
          id="postal-textarea"
          label="Postal Code"
          name="postalCode"
          sx={asterisk}
          error={postalCodeMissing}
          helperText={postalCodeMissing ? 'Missing Postal Code' : ''}
          value={postalCode}
          onChange={handleTextInput}
          disabled={disabled}
          required
        />
      </Grid>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <WorksheetTextField
          id="country-textarea"
          label="Country"
          name="country"
          sx={asterisk}
          error={countryMissing}
          helperText={countryMissing ? 'Missing Country' : ''}
          value={country}
          onChange={handleTextInput}
          disabled={disabled}
          required
        />
      </Grid>
    </Grid>
  );

  return (
    <div>
      {completionStatus.personalInfo && (
        <AddressDetailsContainer container spacing={2}>
          <Heading item xs={12}>
            <animated.div style={animationProps}>Address Details</animated.div>
          </Heading>

          <Grid item xs={12} ref={containerRef}>
            <animated.div style={animationProps}>{InputFields}</animated.div>
            {!completionStatus.addressDetails && completionStatus.personalInfo && (
              <animated.div style={animationProps}>
                <Button
                  disableElevation
                  variant="contained"
                  name="addressDetails"
                  onClick={handleNextClick}
                  sx={{ marginTop: '10px' }}
                >
                  Next
                </Button>
              </animated.div>
            )}
          </Grid>
        </AddressDetailsContainer>
      )}
      {!isXS && completionStatus.addressDetails && <Divider sx={{ marginTop: '50px' }} />}
    </div>
  );
};

interface ChildProps {
  fields: IWorksheetPurchaserInfo;
  completionStatus: ICompletionStatus;
  handleTextInput: any;
  handleDetails: any;
  handleNext: any;
  disabled?: boolean;
  subheading?: string;
  style?: object;
  asterisk?: object;
  submitted?: boolean;
}

const AddressDetailsContainer = styled(Grid)`
  display: flex;
  margin-top: 50px;
  @media (max-width: ${smWidth}) {
    display: block;
  }
`;

export default AddressDetails;
